import { AuthenticationAssuranceLevel, ScopeType, AuditPolicy, IdempotencyPolicy, ConcurrencyPolicy, PrivacyClassification, ActorType, } from './operation.js';
import * as OID from './operation-ids.js';
function organizationPortfolioReadOperation(id, name, labelKey, descriptionKey, permission) {
    return {
        id,
        name,
        labelKey,
        descriptionKey,
        permission,
        permissionId: permission,
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: id,
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    };
}
function settlementOperation(id, name, permission, httpMethods, requiredAal, isReadOnly, makerCheckerRequired = false) {
    return {
        id,
        name,
        labelKey: `operations.${id}.label`,
        descriptionKey: `operations.${id}.description`,
        permission,
        permissionId: permission,
        module: 'INVESTMENT',
        requiredAal,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired,
        idempotencyPolicy: isReadOnly ? IdempotencyPolicy.NOT_APPLICABLE : IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: isReadOnly ? ConcurrencyPolicy.NONE : ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: isReadOnly ? AuditPolicy.READ : AuditPolicy.WRITE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: id,
        allowedActorTypes: [ActorType.USER],
        httpMethods,
        isReadOnly,
        isFinancial: true,
        supportDelegationEligible: false,
    };
}
function complianceBreachWriteOperation(id, name, makerCheckerRequired, concurrencyPolicy = ConcurrencyPolicy.OPTIMISTIC_VERSION) {
    return {
        id,
        name,
        labelKey: `operations.${id}.label`,
        descriptionKey: `operations.${id}.description`,
        permission: 'organization:compliance:manage',
        permissionId: 'organization:compliance:manage',
        module: 'INVESTMENT',
        requiredModules: ['INVESTMENT'],
        requiresEntitledModule: true,
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        allowedScopeKinds: ['ORGANIZATION', 'BRANCH'],
        allowedPersonas: ['ORG_ADMIN'],
        ownershipPolicy: 'ACTIVE_ORGANIZATION',
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: id,
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
        delegationPolicy: 'NOT_ALLOWED',
        accessType: 'WRITE',
    };
}
function positionTransferOperation(id, name, permission, httpMethods, requiredAal, isReadOnly, makerCheckerRequired = false) {
    return settlementOperation(id, name, permission, httpMethods, requiredAal, isReadOnly, makerCheckerRequired);
}
export const INVESTMENT_OPERATIONS = {
    [OID.POSITION_TRANSFERS_LIST]: positionTransferOperation(OID.POSITION_TRANSFERS_LIST, 'List Position Transfers', 'organization:position-transfers:read', ['GET'], AuthenticationAssuranceLevel.AAL1, true),
    [OID.POSITION_TRANSFERS_GET]: positionTransferOperation(OID.POSITION_TRANSFERS_GET, 'Get Position Transfer', 'organization:position-transfers:read', ['GET'], AuthenticationAssuranceLevel.AAL1, true),
    [OID.POSITION_TRANSFERS_ELIGIBILITY]: positionTransferOperation(OID.POSITION_TRANSFERS_ELIGIBILITY, 'Get Position Transfer Eligibility', 'organization:position-transfers:read', ['GET'], AuthenticationAssuranceLevel.AAL1, true),
    [OID.POSITION_TRANSFERS_REQUEST_ELIGIBILITY]: positionTransferOperation(OID.POSITION_TRANSFERS_REQUEST_ELIGIBILITY, 'Get Position Transfer Request Eligibility', 'organization:position-transfers:read', ['GET'], AuthenticationAssuranceLevel.AAL1, true),
    [OID.POSITION_TRANSFERS_REQUEST]: positionTransferOperation(OID.POSITION_TRANSFERS_REQUEST, 'Request Position Transfer', 'organization:position-transfers:request', ['POST'], AuthenticationAssuranceLevel.AAL2, false, true),
    [OID.POSITION_TRANSFERS_APPROVE]: positionTransferOperation(OID.POSITION_TRANSFERS_APPROVE, 'Approve Position Transfer', 'organization:position-transfers:approve', ['POST'], AuthenticationAssuranceLevel.AAL2, false, true),
    [OID.POSITION_TRANSFERS_EXECUTE]: positionTransferOperation(OID.POSITION_TRANSFERS_EXECUTE, 'Execute Position Transfer', 'organization:position-transfers:execute', ['POST'], AuthenticationAssuranceLevel.AAL2, false, true),
    [OID.POSITION_TRANSFERS_CANCEL]: positionTransferOperation(OID.POSITION_TRANSFERS_CANCEL, 'Cancel Position Transfer', 'organization:position-transfers:cancel', ['POST'], AuthenticationAssuranceLevel.AAL2, false, true),
    [OID.POSITION_TRANSFERS_REVERSE]: positionTransferOperation(OID.POSITION_TRANSFERS_REVERSE, 'Reverse Position Transfer', 'organization:position-transfers:reverse', ['POST'], AuthenticationAssuranceLevel.AAL2, false, true),
    [OID.SETTLEMENT_INSTRUCTIONS_LIST]: settlementOperation(OID.SETTLEMENT_INSTRUCTIONS_LIST, 'List Settlement Instructions', 'organization:settlement:read', ['GET'], AuthenticationAssuranceLevel.AAL1, true),
    [OID.SETTLEMENT_FAILURES_LIST]: settlementOperation(OID.SETTLEMENT_FAILURES_LIST, 'List Failed Settlement Instructions', 'organization:settlement:read', ['GET'], AuthenticationAssuranceLevel.AAL1, true),
    [OID.SETTLEMENT_INSTRUCTIONS_GET]: settlementOperation(OID.SETTLEMENT_INSTRUCTIONS_GET, 'Get Settlement Instruction', 'organization:settlement:read', ['GET'], AuthenticationAssuranceLevel.AAL1, true),
    [OID.SETTLEMENT_INSTRUCTIONS_AVAILABILITY]: settlementOperation(OID.SETTLEMENT_INSTRUCTIONS_AVAILABILITY, 'Get Settlement Availability', 'organization:settlement:read', ['GET'], AuthenticationAssuranceLevel.AAL1, true),
    [OID.SETTLEMENT_INSTRUCTIONS_CREATE]: settlementOperation(OID.SETTLEMENT_INSTRUCTIONS_CREATE, 'Create Settlement Instruction', 'organization:settlement:create', ['POST'], AuthenticationAssuranceLevel.AAL1, false),
    [OID.SETTLEMENT_INSTRUCTIONS_CONFIRM]: settlementOperation(OID.SETTLEMENT_INSTRUCTIONS_CONFIRM, 'Confirm Settlement Instruction', 'organization:settlement:confirm', ['POST'], AuthenticationAssuranceLevel.AAL2, false, true),
    [OID.SETTLEMENT_INSTRUCTIONS_CORRECT]: settlementOperation(OID.SETTLEMENT_INSTRUCTIONS_CORRECT, 'Correct Settlement Instruction', 'organization:settlement:correct', ['POST'], AuthenticationAssuranceLevel.AAL2, false, true),
    [OID.SETTLEMENT_INSTRUCTIONS_CANCEL]: settlementOperation(OID.SETTLEMENT_INSTRUCTIONS_CANCEL, 'Cancel Settlement Instruction', 'organization:settlement:cancel', ['POST'], AuthenticationAssuranceLevel.AAL2, false, true),
    [OID.COMPLIANCE_BREACHES_LIST]: {
        id: OID.COMPLIANCE_BREACHES_LIST,
        name: 'List Compliance Breaches',
        labelKey: 'operations.investment.compliance.breaches.list.label',
        descriptionKey: 'operations.investment.compliance.breaches.list.description',
        permission: 'organization:compliance:read',
        permissionId: 'organization:compliance:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'compliance.breaches.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.COMPLIANCE_BREACHES_GET]: {
        id: OID.COMPLIANCE_BREACHES_GET,
        name: 'Get Compliance Breach',
        labelKey: 'operations.investment.compliance.breaches.get.label',
        descriptionKey: 'operations.investment.compliance.breaches.get.description',
        permission: 'organization:compliance:read',
        permissionId: 'organization:compliance:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'compliance.breaches.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.COMPLIANCE_BREACHES_OPEN]: complianceBreachWriteOperation(OID.COMPLIANCE_BREACHES_OPEN, 'Open Compliance Breach', false, ConcurrencyPolicy.OPTIMISTIC_VERSION),
    [OID.COMPLIANCE_BREACHES_REMEDIATE]: complianceBreachWriteOperation(OID.COMPLIANCE_BREACHES_REMEDIATE, 'Remediate Compliance Breach', false),
    [OID.COMPLIANCE_BREACHES_WAIVE]: complianceBreachWriteOperation(OID.COMPLIANCE_BREACHES_WAIVE, 'Waive Compliance Breach', true),
    [OID.RISK_EXPOSURES_LIST]: {
        id: OID.RISK_EXPOSURES_LIST,
        name: 'List Risk Exposures',
        labelKey: 'operations.investment.risk.exposures.list.label',
        descriptionKey: 'operations.investment.risk.exposures.list.description',
        permission: 'organization:risk:read',
        permissionId: 'organization:risk:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'risk.exposures.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.CLIENTS_LIST]: organizationPortfolioReadOperation(OID.CLIENTS_LIST, 'List Clients', 'card.clientsData.title', 'card.clientsData.description', 'organization:clients:read'),
    [OID.CLIENTS_GET]: organizationPortfolioReadOperation(OID.CLIENTS_GET, 'Get Client', 'card.clientsData.title', 'card.clientsData.description', 'organization:clients:read'),
    [OID.PORTFOLIOS_LIST]: organizationPortfolioReadOperation(OID.PORTFOLIOS_LIST, 'List Portfolios', 'card.portfoliosData.title', 'card.portfoliosData.description', 'organization:portfolios:read'),
    [OID.PORTFOLIOS_GET]: organizationPortfolioReadOperation(OID.PORTFOLIOS_GET, 'Get Portfolio', 'card.portfoliosData.title', 'card.portfoliosData.description', 'organization:portfolios:read'),
    [OID.POSITIONS_LIST]: organizationPortfolioReadOperation(OID.POSITIONS_LIST, 'List Positions', 'card.positions.title', 'card.positions.description', 'organization:holdings:read'),
    [OID.POSITIONS_GET]: organizationPortfolioReadOperation(OID.POSITIONS_GET, 'Get Position', 'card.positions.title', 'card.positions.description', 'organization:holdings:read'),
    [OID.CASH_POSITIONS_LIST]: {
        id: OID.CASH_POSITIONS_LIST,
        name: 'List Cash Positions',
        labelKey: 'card.cash.title',
        descriptionKey: 'card.cash.description',
        permission: 'organization:holdings:read',
        permissionId: 'organization:holdings:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.cash.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.CASH_POSITIONS_GET]: {
        id: OID.CASH_POSITIONS_GET,
        name: 'Get Cash Position',
        labelKey: 'card.cash.title',
        descriptionKey: 'card.cash.description',
        permission: 'organization:holdings:read',
        permissionId: 'organization:holdings:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.cash.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.TRANSACTION_CREATE]: {
        id: OID.TRANSACTION_CREATE,
        name: 'Create Transaction',
        labelKey: 'operations.investment.transaction.create.label',
        descriptionKey: 'operations.investment.transaction.create.description',
        permission: 'organization:transactions:create',
        permissionId: 'organization:transactions:create',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: true,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.create',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.TRANSACTION_LIST]: {
        id: OID.TRANSACTION_LIST,
        name: 'List Transactions',
        labelKey: 'operations.investment.transaction.list.label',
        descriptionKey: 'operations.investment.transaction.list.description',
        permission: 'organization:transactions:list',
        permissionId: 'organization:transactions:list',
        permissionByActorType: { [ActorType.CLIENT]: 'client:transactions:read-own' },
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.list',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.TRANSACTION_EXPORT]: {
        id: OID.TRANSACTION_EXPORT,
        name: 'Export Transactions',
        labelKey: 'operations.investment.transaction.export.label',
        descriptionKey: 'operations.investment.transaction.export.description',
        permission: 'organization:transactions:export',
        permissionId: 'organization:transactions:export',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.export',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.TRANSACTION_GET]: {
        id: OID.TRANSACTION_GET,
        name: 'Get Transaction',
        labelKey: 'operations.investment.transaction.get.label',
        descriptionKey: 'operations.investment.transaction.get.description',
        permission: 'organization:transactions:read',
        permissionId: 'organization:transactions:read',
        permissionByActorType: { [ActorType.CLIENT]: 'client:transactions:read-own' },
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.get',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.TRANSACTION_SUBMIT]: {
        id: OID.TRANSACTION_SUBMIT,
        name: 'Submit Transaction',
        labelKey: 'operations.investment.transaction.submit.label',
        descriptionKey: 'operations.investment.transaction.submit.description',
        permission: 'organization:transactions:submit',
        permissionId: 'organization:transactions:submit',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.submit',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.TRANSACTION_APPROVE]: {
        id: OID.TRANSACTION_APPROVE,
        name: 'Approve Transaction',
        labelKey: 'operations.investment.transaction.approve.label',
        descriptionKey: 'operations.investment.transaction.approve.description',
        permission: 'organization:transactions:approve',
        permissionId: 'organization:transactions:approve',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.transaction.approve.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.approve',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.TRANSACTION_REJECT]: {
        id: OID.TRANSACTION_REJECT,
        name: 'Reject Transaction',
        labelKey: 'operations.investment.transaction.reject.label',
        descriptionKey: 'operations.investment.transaction.reject.description',
        permission: 'organization:transactions:reject',
        permissionId: 'organization:transactions:reject',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.transaction.reject.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.reject',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.TRANSACTION_WITHDRAW]: {
        id: OID.TRANSACTION_WITHDRAW,
        name: 'Withdraw Transaction',
        labelKey: 'operations.investment.transaction.withdraw.label',
        descriptionKey: 'operations.investment.transaction.withdraw.description',
        permission: 'organization:transactions:withdraw',
        permissionId: 'organization:transactions:withdraw',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.withdraw',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.TRANSACTION_POST]: {
        id: OID.TRANSACTION_POST,
        name: 'Post Transaction',
        labelKey: 'operations.investment.transaction.post.label',
        descriptionKey: 'operations.investment.transaction.post.description',
        permission: 'organization:transactions:post',
        permissionId: 'organization:transactions:post',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.transaction.post.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.post',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.TRANSACTION_REPAIR]: {
        id: OID.TRANSACTION_REPAIR,
        name: 'Repair Transaction',
        labelKey: 'operations.investment.transaction.repair.label',
        descriptionKey: 'operations.investment.transaction.repair.description',
        permission: 'organization:transactions:repair',
        permissionId: 'organization:transactions:repair',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.transaction.repair.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.repair',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.TRANSACTION_REPOST]: {
        id: OID.TRANSACTION_REPOST,
        name: 'Repost Transaction',
        labelKey: 'operations.investment.transaction.repost.label',
        descriptionKey: 'operations.investment.transaction.repost.description',
        permission: 'organization:transactions:repost',
        permissionId: 'organization:transactions:repost',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.transaction.repost.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.repost',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.TRANSACTION_REVERSE]: {
        id: OID.TRANSACTION_REVERSE,
        name: 'Reverse Transaction',
        labelKey: 'operations.investment.transaction.reverse.label',
        descriptionKey: 'operations.investment.transaction.reverse.description',
        permission: 'organization:transactions:reverse',
        permissionId: 'organization:transactions:reverse',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.transaction.reverse.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.reverse',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.TRANSACTION_LOTS]: {
        id: OID.TRANSACTION_LOTS,
        name: 'View Transaction Lots',
        labelKey: 'operations.investment.transaction.lots.label',
        descriptionKey: 'operations.investment.transaction.lots.description',
        permission: 'organization:transactions:read',
        permissionId: 'organization:transactions:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.lots',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.TRANSACTION_DISPOSALS]: {
        id: OID.TRANSACTION_DISPOSALS,
        name: 'View Transaction Disposals',
        labelKey: 'operations.investment.transaction.disposals.label',
        descriptionKey: 'operations.investment.transaction.disposals.description',
        permission: 'organization:transactions:read',
        permissionId: 'organization:transactions:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.disposals',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.TRANSACTION_FACTS]: {
        id: OID.TRANSACTION_FACTS,
        name: 'View Transaction Facts',
        labelKey: 'operations.investment.transaction.facts.label',
        descriptionKey: 'operations.investment.transaction.facts.description',
        permission: 'organization:transactions:read',
        permissionId: 'organization:transactions:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.transaction.facts',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.INSTRUMENT_LIST]: {
        id: OID.INSTRUMENT_LIST,
        name: 'List Instruments',
        labelKey: 'operations.investment.instrument.list.label',
        descriptionKey: 'operations.investment.instrument.list.description',
        permission: 'organization:instruments:read',
        permissionId: 'organization:instruments:read',
        module: 'INSTRUMENTS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.SYSTEM, ScopeType.PLATFORM],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.instrument.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.INSTRUMENT_GET]: {
        id: OID.INSTRUMENT_GET,
        name: 'Get Instrument',
        labelKey: 'operations.investment.instrument.get.label',
        descriptionKey: 'operations.investment.instrument.get.description',
        permission: 'organization:instruments:read',
        permissionId: 'organization:instruments:read',
        module: 'INSTRUMENTS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.SYSTEM, ScopeType.PLATFORM],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.instrument.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.INSTRUMENT_CREATE]: {
        id: OID.INSTRUMENT_CREATE,
        name: 'Create Instrument',
        labelKey: 'operations.investment.instrument.create.label',
        descriptionKey: 'operations.investment.instrument.create.description',
        permission: 'organization:instruments:manage',
        permissionId: 'organization:instruments:manage',
        module: 'INSTRUMENTS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.PLATFORM, ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.instrument.create.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.instrument.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.INSTRUMENT_UPDATE]: {
        id: OID.INSTRUMENT_UPDATE,
        name: 'Update Instrument',
        labelKey: 'operations.investment.instrument.update.label',
        descriptionKey: 'operations.investment.instrument.update.description',
        permission: 'organization:instruments:manage',
        permissionId: 'organization:instruments:manage',
        module: 'INSTRUMENTS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.PLATFORM, ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.instrument.update.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.instrument.update',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PUT', 'PATCH'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.INSTRUMENT_OVERRIDE_CREATE]: {
        id: OID.INSTRUMENT_OVERRIDE_CREATE,
        name: 'Create Instrument Override',
        labelKey: 'operations.investment.instrument.override.create.label',
        descriptionKey: 'operations.investment.instrument.override.create.description',
        permission: 'organization:instruments:manage',
        permissionId: 'organization:instruments:manage',
        module: 'INSTRUMENTS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.instrument.override.create.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.instrument.override.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.INSTRUMENT_OVERRIDE_LIST]: {
        id: OID.INSTRUMENT_OVERRIDE_LIST,
        name: 'List Instrument Overrides',
        labelKey: 'operations.investment.instrument.override.list.label',
        descriptionKey: 'operations.investment.instrument.override.list.description',
        permission: 'organization:instruments:read',
        permissionId: 'organization:instruments:read',
        module: 'INSTRUMENTS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.instrument.override.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.INSTRUMENT_DELETE]: {
        id: OID.INSTRUMENT_DELETE,
        name: 'Delete Instrument',
        labelKey: 'operations.investment.instrument.delete.label',
        descriptionKey: 'operations.investment.instrument.delete.description',
        permission: 'security-master:destructive-delete',
        permissionId: 'security-master:destructive-delete',
        module: 'INSTRUMENTS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.PLATFORM, ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.instrument.delete.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.instrument.delete',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['DELETE'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.INSTRUMENT_BULK_UPSERT]: {
        id: OID.INSTRUMENT_BULK_UPSERT,
        name: 'Bulk Upsert Instruments',
        labelKey: 'operations.investment.instrument.bulkUpsert.label',
        descriptionKey: 'operations.investment.instrument.bulkUpsert.description',
        permission: 'organization:instruments:manage',
        permissionId: 'organization:instruments:manage',
        module: 'INSTRUMENTS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.PLATFORM, ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.instrument.bulkUpsert.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.instrument.bulkUpsert',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.INSTRUMENT_OVERRIDE_UPDATE]: {
        id: OID.INSTRUMENT_OVERRIDE_UPDATE,
        name: 'Update Instrument Override',
        labelKey: 'operations.investment.instrument.override.update.label',
        descriptionKey: 'operations.investment.instrument.override.update.description',
        permission: 'organization:instruments:manage',
        permissionId: 'organization:instruments:manage',
        module: 'INSTRUMENTS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.instrument.override.update.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.instrument.override.update',
        allowedActorTypes: [ActorType.USER],
        // The portfolio account lifecycle also owns governed cash-movement
        // decisions, posting, and reversal under the same organization-only
        // portfolio update authority.
        httpMethods: ['PATCH', 'POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.INSTRUMENT_OVERRIDE_DELETE]: {
        id: OID.INSTRUMENT_OVERRIDE_DELETE,
        name: 'Delete Instrument Override',
        labelKey: 'operations.investment.instrument.override.delete.label',
        descriptionKey: 'operations.investment.instrument.override.delete.description',
        permission: 'organization:instruments:manage',
        permissionId: 'organization:instruments:manage',
        module: 'INSTRUMENTS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.instrument.override.delete.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.instrument.override.delete',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['DELETE'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.MARKET_DATA_CREATE]: {
        id: OID.MARKET_DATA_CREATE,
        name: 'Create Market Data Entry',
        labelKey: 'operations.investment.marketData.create.label',
        descriptionKey: 'operations.investment.marketData.create.description',
        permission: 'organization:market-data:manage',
        permissionId: 'organization:market-data:manage',
        module: 'MARKET_DATA',
        requiredModules: ['MARKET_DATA'],
        requiresEntitledModule: true,
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.PLATFORM, ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.marketData.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.MARKET_DATA_LIST]: {
        id: OID.MARKET_DATA_LIST,
        name: 'List Market Data',
        labelKey: 'operations.investment.marketData.list.label',
        descriptionKey: 'operations.investment.marketData.list.description',
        permission: 'organization:market-data:read',
        permissionId: 'organization:market-data:read',
        module: 'MARKET_DATA',
        requiredModules: ['MARKET_DATA'],
        requiresEntitledModule: true,
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.SYSTEM, ScopeType.PLATFORM],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.marketData.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.MARKET_DATA_GET]: {
        id: OID.MARKET_DATA_GET,
        name: 'Get Market Data Entry',
        labelKey: 'operations.investment.marketData.get.label',
        descriptionKey: 'operations.investment.marketData.get.description',
        permission: 'organization:market-data:read',
        permissionId: 'organization:market-data:read',
        module: 'MARKET_DATA',
        requiredModules: ['MARKET_DATA'],
        requiresEntitledModule: true,
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.SYSTEM, ScopeType.PLATFORM],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.marketData.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.MARKET_DATA_UPDATE]: {
        id: OID.MARKET_DATA_UPDATE,
        name: 'Update Market Data Entry',
        labelKey: 'operations.investment.marketData.update.label',
        descriptionKey: 'operations.investment.marketData.update.description',
        permission: 'organization:market-data:manage',
        permissionId: 'organization:market-data:manage',
        module: 'MARKET_DATA',
        requiredModules: ['MARKET_DATA'],
        requiresEntitledModule: true,
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.PLATFORM, ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.marketData.update.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.marketData.update',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PATCH'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.MARKET_DATA_DELETE]: {
        id: OID.MARKET_DATA_DELETE,
        name: 'Delete Market Data Entry',
        labelKey: 'operations.investment.marketData.delete.label',
        descriptionKey: 'operations.investment.marketData.delete.description',
        permission: 'organization:market-data:manage',
        permissionId: 'organization:market-data:manage',
        module: 'MARKET_DATA',
        requiredModules: ['MARKET_DATA'],
        requiresEntitledModule: true,
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.PLATFORM, ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.marketData.delete.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.marketData.delete',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['DELETE'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.MARKET_DATA_BULK_UPSERT]: {
        id: OID.MARKET_DATA_BULK_UPSERT,
        name: 'Bulk Upsert Market Data',
        labelKey: 'operations.investment.marketData.bulkUpsert.label',
        descriptionKey: 'operations.investment.marketData.bulkUpsert.description',
        permission: 'organization:market-data:manage',
        permissionId: 'organization:market-data:manage',
        module: 'INSTRUMENTS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.PLATFORM, ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.marketData.bulkUpsert.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.INTERNAL,
        telemetryId: 'investment.marketData.bulkUpsert',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.PORTFOLIO_ACCOUNT_LIST]: {
        id: OID.PORTFOLIO_ACCOUNT_LIST,
        name: 'List Portfolio Accounts',
        labelKey: 'operations.investment.portfolioAccount.list.label',
        descriptionKey: 'operations.investment.portfolioAccount.list.description',
        permission: 'portfolios:view',
        permissionId: 'organization:portfolios:list',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        forcesClientScope: true,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.portfolioAccount.list',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
        requestedScope: {
            clientAccountId: [{ source: 'query', field: 'party' }],
        },
    },
    [OID.PORTFOLIO_ACCOUNT_GET]: {
        id: OID.PORTFOLIO_ACCOUNT_GET,
        name: 'Get Portfolio Account',
        labelKey: 'operations.investment.portfolioAccount.get.label',
        descriptionKey: 'operations.investment.portfolioAccount.get.description',
        permission: 'organization:portfolios:read',
        permissionId: 'organization:portfolios:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        forcesClientScope: true,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.portfolioAccount.get',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
        requestedScope: {
            portfolioId: [{ source: 'path', field: 'id' }],
        },
    },
    [OID.PORTFOLIO_ACCOUNT_GET_BALANCE]: {
        id: OID.PORTFOLIO_ACCOUNT_GET_BALANCE,
        name: 'Get Portfolio Account Balance',
        labelKey: 'operations.investment.portfolioAccount.getBalance.label',
        descriptionKey: 'operations.investment.portfolioAccount.getBalance.description',
        permission: 'organization:portfolios:read',
        permissionId: 'organization:portfolios:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        forcesClientScope: true,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.portfolioAccount.getBalance',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
        requestedScope: {
            portfolioId: [{ source: 'path', field: 'id' }],
        },
    },
    [OID.PORTFOLIO_ACCOUNT_CREATE]: {
        id: OID.PORTFOLIO_ACCOUNT_CREATE,
        name: 'Create Portfolio Account',
        labelKey: 'operations.investment.portfolioAccount.create.label',
        descriptionKey: 'operations.investment.portfolioAccount.create.description',
        permission: 'portfolios:manage',
        permissionId: 'organization:portfolios:create',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
            clientAccountId: [
                { source: 'path', field: 'clientAccountId' },
                { source: 'query', field: 'clientAccountId' },
            ],
            portfolioId: [
                { source: 'path', field: 'portfolioId' },
                { source: 'query', field: 'portfolioId' },
            ],
        },
        forcesClientScope: true,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.portfolioAccount.create',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.PORTFOLIO_ACCOUNT_UPDATE]: {
        id: OID.PORTFOLIO_ACCOUNT_UPDATE,
        name: 'Update Portfolio Account',
        labelKey: 'operations.investment.portfolioAccount.update.label',
        descriptionKey: 'operations.investment.portfolioAccount.update.description',
        permission: 'organization:portfolios:update',
        permissionId: 'organization:portfolios:update',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        forcesClientScope: true,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.portfolioAccount.update.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.portfolioAccount.update',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PATCH'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
        requestedScope: {
            portfolioId: [{ source: 'path', field: 'id' }],
        },
    },
    [OID.PORTFOLIO_ACCOUNT_SIGNATORIES_LIST]: {
        id: OID.PORTFOLIO_ACCOUNT_SIGNATORIES_LIST,
        name: 'List Portfolio Account Signatories',
        labelKey: 'operations.investment.portfolioAccount.signatories.list.label',
        descriptionKey: 'operations.investment.portfolioAccount.signatories.list.description',
        permission: 'organization:portfolios:read',
        permissionId: 'organization:portfolios:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        forcesClientScope: true,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.portfolioAccount.signatories.list',
        allowedActorTypes: [ActorType.USER, ActorType.CLIENT, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
        requestedScope: {
            portfolioId: [{ source: 'path', field: 'id' }],
        },
    },
    [OID.PORTFOLIO_ACCOUNT_SIGNATORY_CREATE]: {
        id: OID.PORTFOLIO_ACCOUNT_SIGNATORY_CREATE,
        name: 'Add Portfolio Account Signatory',
        labelKey: 'operations.investment.portfolioAccount.signatory.create.label',
        descriptionKey: 'operations.investment.portfolioAccount.signatory.create.description',
        permission: 'organization:portfolios:update',
        permissionId: 'organization:portfolios:update',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        forcesClientScope: true,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.portfolioAccount.signatory.create.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.portfolioAccount.signatory.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
        requestedScope: {
            portfolioId: [{ source: 'path', field: 'id' }],
        },
    },
    [OID.PORTFOLIO_ACCOUNT_SIGNATORY_DELETE]: {
        id: OID.PORTFOLIO_ACCOUNT_SIGNATORY_DELETE,
        name: 'Remove Portfolio Account Signatory',
        labelKey: 'operations.investment.portfolioAccount.signatory.delete.label',
        descriptionKey: 'operations.investment.portfolioAccount.signatory.delete.description',
        permission: 'organization:portfolios:update',
        permissionId: 'organization:portfolios:update',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH, ScopeType.CLIENT, ScopeType.PORTFOLIO],
        forcesClientScope: true,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.portfolioAccount.signatory.delete.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.portfolioAccount.signatory.delete',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['DELETE'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
        requestedScope: {
            portfolioId: [{ source: 'path', field: 'id' }],
        },
    },
    [OID.CLIENT_ACCOUNT_LIST]: {
        id: OID.CLIENT_ACCOUNT_LIST,
        name: 'List Client Accounts',
        labelKey: 'operations.platform.clientAccount.list.label',
        descriptionKey: 'operations.platform.clientAccount.list.description',
        permission: 'clients:view',
        permissionId: 'organization:client-accounts:list',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'platform.clientAccount.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.CLIENT_ACCOUNT_GET]: {
        id: OID.CLIENT_ACCOUNT_GET,
        name: 'Get Client Account',
        labelKey: 'operations.platform.clientAccount.get.label',
        descriptionKey: 'operations.platform.clientAccount.get.description',
        permission: 'organization:client-accounts:read',
        permissionId: 'organization:client-accounts:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'platform.clientAccount.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.CLIENT_ACCOUNT_CREATE]: {
        id: OID.CLIENT_ACCOUNT_CREATE,
        name: 'Create Client Account',
        labelKey: 'operations.platform.clientAccount.create.label',
        descriptionKey: 'operations.platform.clientAccount.create.description',
        permission: 'clients:manage',
        permissionId: 'organization:client-accounts:create',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.platform.clientAccount.create.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'platform.clientAccount.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CLIENT_ACCOUNT_UPDATE]: {
        id: OID.CLIENT_ACCOUNT_UPDATE,
        name: 'Update Client Account',
        labelKey: 'operations.platform.clientAccount.update.label',
        descriptionKey: 'operations.platform.clientAccount.update.description',
        permission: 'organization:client-accounts:update',
        permissionId: 'organization:client-accounts:update',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.platform.clientAccount.update.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'platform.clientAccount.update',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PUT', 'PATCH'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CLIENT_ACCOUNT_DOCUMENTS_LIST]: {
        id: OID.CLIENT_ACCOUNT_DOCUMENTS_LIST,
        name: 'List Client Account Documents',
        labelKey: 'operations.platform.clientAccount.documents.list.label',
        descriptionKey: 'operations.platform.clientAccount.documents.list.description',
        permission: 'organization:client-accounts:read',
        permissionId: 'organization:client-accounts:read',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'platform.clientAccount.documents.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.CLIENT_ACCOUNT_DOCUMENT_CREATE]: {
        id: OID.CLIENT_ACCOUNT_DOCUMENT_CREATE,
        name: 'Upload Client Account Document',
        labelKey: 'operations.platform.clientAccount.document.create.label',
        descriptionKey: 'operations.platform.clientAccount.document.create.description',
        permission: 'organization:client-accounts:update',
        permissionId: 'organization:client-accounts:update',
        module: 'INVESTMENT',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.platform.clientAccount.document.create.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'platform.clientAccount.document.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CORPORATE_ACTION_LIST]: {
        id: OID.CORPORATE_ACTION_LIST,
        name: 'List Corporate Actions',
        labelKey: 'operations.investment.corporateAction.list.label',
        descriptionKey: 'operations.investment.corporateAction.list.description',
        permission: 'organization:corporate-actions:read',
        permissionId: 'organization:corporate-actions:read',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.CORPORATE_ACTION_CREATE]: {
        id: OID.CORPORATE_ACTION_CREATE,
        name: 'Create Corporate Action',
        labelKey: 'operations.investment.corporateAction.create.label',
        descriptionKey: 'operations.investment.corporateAction.create.description',
        permission: 'organization:corporate-actions:manage',
        permissionId: 'organization:corporate-actions:manage',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.corporateAction.create.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CORPORATE_ACTION_UPDATE]: {
        id: OID.CORPORATE_ACTION_UPDATE,
        name: 'Update Corporate Action',
        labelKey: 'operations.investment.corporateAction.update.label',
        descriptionKey: 'operations.investment.corporateAction.update.description',
        permission: 'organization:corporate-actions:manage',
        permissionId: 'organization:corporate-actions:manage',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.corporateAction.update.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.update',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['PUT', 'PATCH'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CORPORATE_ACTION_SUBMIT]: {
        id: OID.CORPORATE_ACTION_SUBMIT,
        name: 'Submit Corporate Action',
        labelKey: 'action.submit',
        descriptionKey: 'operations.investment.corporateAction.update.description',
        permission: 'organization:corporate-actions:manage',
        permissionId: 'organization:corporate-actions:manage',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [{ source: 'path', field: 'organizationId' }],
            branchId: [{ source: 'path', field: 'branchId' }],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.submit',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CORPORATE_ACTION_APPROVE]: {
        id: OID.CORPORATE_ACTION_APPROVE,
        name: 'Approve Corporate Action',
        labelKey: 'action.approve',
        descriptionKey: 'operations.investment.corporateAction.update.description',
        permission: 'organization:corporate-actions:manage',
        permissionId: 'organization:corporate-actions:manage',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [{ source: 'path', field: 'organizationId' }],
            branchId: [{ source: 'path', field: 'branchId' }],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.approve',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CORPORATE_ACTION_REJECT]: {
        id: OID.CORPORATE_ACTION_REJECT,
        name: 'Reject Corporate Action',
        labelKey: 'action.reject',
        descriptionKey: 'operations.investment.corporateAction.update.description',
        permission: 'organization:corporate-actions:manage',
        permissionId: 'organization:corporate-actions:manage',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [{ source: 'path', field: 'organizationId' }],
            branchId: [{ source: 'path', field: 'branchId' }],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.reject',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CORPORATE_ACTION_CANCEL]: {
        id: OID.CORPORATE_ACTION_CANCEL,
        name: 'Cancel Corporate Action',
        labelKey: 'action.cancel',
        descriptionKey: 'operations.investment.corporateAction.update.description',
        permission: 'organization:corporate-actions:manage',
        permissionId: 'organization:corporate-actions:manage',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [{ source: 'path', field: 'organizationId' }],
            branchId: [{ source: 'path', field: 'branchId' }],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.cancel',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CORPORATE_ACTION_GET]: {
        id: OID.CORPORATE_ACTION_GET,
        name: 'Get Corporate Action',
        labelKey: 'operations.investment.corporateAction.get.label',
        descriptionKey: 'operations.investment.corporateAction.get.description',
        permission: 'organization:corporate-actions:read',
        permissionId: 'organization:corporate-actions:read',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.CORPORATE_ACTION_PROCESS]: {
        id: OID.CORPORATE_ACTION_PROCESS,
        name: 'Process Corporate Action Entitlements',
        labelKey: 'operations.investment.corporateAction.process.label',
        descriptionKey: 'operations.investment.corporateAction.process.description',
        permission: 'organization:corporate-actions:manage',
        permissionId: 'organization:corporate-actions:manage',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.corporateAction.process.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.process',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CORPORATE_ACTION_ENTITLEMENTS_LIST]: {
        id: OID.CORPORATE_ACTION_ENTITLEMENTS_LIST,
        name: 'List Corporate Action Entitlements',
        labelKey: 'operations.investment.corporateAction.entitlements.list.label',
        descriptionKey: 'operations.investment.corporateAction.entitlements.list.description',
        permission: 'organization:corporate-actions:read',
        permissionId: 'organization:corporate-actions:read',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.entitlements.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.CORPORATE_ACTION_ELECT]: {
        id: OID.CORPORATE_ACTION_ELECT,
        name: 'Record Corporate Action Election',
        labelKey: 'operations.investment.corporateAction.elect.label',
        descriptionKey: 'operations.investment.corporateAction.elect.description',
        permission: 'organization:corporate-actions:manage',
        permissionId: 'organization:corporate-actions:manage',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.corporateAction.elect.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.elect',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CORPORATE_ACTION_ALLOCATE]: {
        id: OID.CORPORATE_ACTION_ALLOCATE,
        name: 'Allocate Corporate Action',
        labelKey: 'operations.investment.corporateAction.allocate.label',
        descriptionKey: 'operations.investment.corporateAction.allocate.description',
        permission: 'organization:corporate-actions:manage',
        permissionId: 'organization:corporate-actions:manage',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL2,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: true,
        makerCheckerReasonKey: 'operations.investment.corporateAction.allocate.makerChecker.reason',
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.OPTIMISTIC_VERSION,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.allocate',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.CORPORATE_ACTION_EX_DATE_CLASSIFY]: {
        id: OID.CORPORATE_ACTION_EX_DATE_CLASSIFY,
        name: 'Classify Ex-Date Trades',
        labelKey: 'operations.investment.corporateAction.exDate.classify.label',
        descriptionKey: 'operations.investment.corporateAction.exDate.classify.description',
        permission: 'organization:corporate-actions:read',
        permissionId: 'organization:corporate-actions:read',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.exDate.classify',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.CORPORATE_ACTION_EX_DATE_VERIFY]: {
        id: OID.CORPORATE_ACTION_EX_DATE_VERIFY,
        name: 'Verify Ex-Date Exclusion',
        labelKey: 'operations.investment.corporateAction.exDate.verify.label',
        descriptionKey: 'operations.investment.corporateAction.exDate.verify.description',
        permission: 'organization:corporate-actions:read',
        permissionId: 'organization:corporate-actions:read',
        module: 'CORPORATE_ACTIONS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.corporateAction.exDate.verify',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: false,
        supportDelegationEligible: true,
    },
    [OID.IMPORT_RECONCILE]: {
        id: OID.IMPORT_RECONCILE,
        name: 'Reconcile Import Job',
        labelKey: 'operations.import.reconcile.label',
        descriptionKey: 'operations.import.reconcile.description',
        permission: 'organization:imports:validate',
        permissionId: 'organization:imports:validate',
        module: 'IMPORT',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'import.reconcile',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: false,
        supportDelegationEligible: false,
    },
    [OID.FACT_LIST]: {
        id: OID.FACT_LIST,
        name: 'List Facts',
        labelKey: 'operations.investment.fact.list.label',
        descriptionKey: 'operations.investment.fact.list.description',
        permission: 'organization:facts:read',
        permissionId: 'organization:facts:read',
        module: 'FACTS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.FACT_GET]: {
        id: OID.FACT_GET,
        name: 'Get Fact',
        labelKey: 'operations.investment.fact.get.label',
        descriptionKey: 'operations.investment.fact.get.description',
        permission: 'organization:facts:read',
        permissionId: 'organization:facts:read',
        module: 'FACTS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.FACT_CREATE]: {
        id: OID.FACT_CREATE,
        name: 'Create Fact',
        labelKey: 'operations.investment.fact.create.label',
        descriptionKey: 'operations.investment.fact.create.description',
        permission: 'organization:facts:manage',
        permissionId: 'organization:facts:manage',
        module: 'FACTS',
        requiresEntitledModule: false,
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.REQUIRED,
        concurrencyPolicy: ConcurrencyPolicy.ADVISORY_LOCK,
        auditPolicy: AuditPolicy.SENSITIVE,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.create',
        allowedActorTypes: [ActorType.USER],
        httpMethods: ['POST'],
        isReadOnly: false,
        isFinancial: true,
        supportDelegationEligible: false,
    },
    [OID.FACT_POSITION]: {
        id: OID.FACT_POSITION,
        name: 'Get Position Facts',
        labelKey: 'operations.investment.fact.position.label',
        descriptionKey: 'operations.investment.fact.position.description',
        permission: 'organization:facts:read',
        permissionId: 'organization:facts:read',
        module: 'FACTS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.position',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.FACT_VALUATION]: {
        id: OID.FACT_VALUATION,
        name: 'Get Valuation Facts',
        labelKey: 'operations.investment.fact.valuation.label',
        descriptionKey: 'operations.investment.fact.valuation.description',
        permission: 'organization:facts:read',
        permissionId: 'organization:facts:read',
        module: 'FACTS',
        requiresEntitledModule: false,
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.valuation',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.FACT_MOVEMENT]: {
        id: OID.FACT_MOVEMENT,
        name: 'Get Movement Facts',
        labelKey: 'operations.investment.fact.movement.label',
        descriptionKey: 'operations.investment.fact.movement.description',
        permission: 'organization:facts:read',
        permissionId: 'organization:facts:read',
        module: 'FACTS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.movement',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.FACT_REALIZED]: {
        id: OID.FACT_REALIZED,
        name: 'Get Realized Gain/Loss Facts',
        labelKey: 'operations.investment.fact.realized.label',
        descriptionKey: 'operations.investment.fact.realized.description',
        permission: 'organization:facts:read',
        permissionId: 'organization:facts:read',
        module: 'FACTS',
        requiresEntitledModule: false,
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.realized',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.FACT_CGT]: {
        id: OID.FACT_CGT,
        name: 'Get Capital Gains Tax Facts',
        labelKey: 'operations.investment.fact.cgt.label',
        descriptionKey: 'operations.investment.fact.cgt.description',
        permission: 'organization:facts:read',
        permissionId: 'organization:facts:read',
        module: 'FACTS',
        requiresEntitledModule: false,
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.cgt',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.FACT_NFRS]: {
        id: OID.FACT_NFRS,
        name: 'Get NFRS Facts',
        labelKey: 'operations.investment.fact.nfrs.label',
        descriptionKey: 'operations.investment.fact.nfrs.description',
        permission: 'organization:facts:read',
        permissionId: 'organization:facts:read',
        module: 'FACTS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.nfrs',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.FACT_BATCHES_LIST]: {
        id: OID.FACT_BATCHES_LIST,
        name: 'List Fact Batches',
        labelKey: 'operations.investment.fact.batches.list.label',
        descriptionKey: 'operations.investment.fact.batches.list.description',
        permission: 'organization:facts:read',
        permissionId: 'organization:facts:read',
        module: 'FACTS',
        requiresEntitledModule: false,
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.batches.list',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.FACT_BATCH_GET]: {
        id: OID.FACT_BATCH_GET,
        name: 'Get Fact Batch',
        labelKey: 'operations.investment.fact.batch.get.label',
        descriptionKey: 'operations.investment.fact.batch.get.description',
        permission: 'organization:facts:read',
        permissionId: 'organization:facts:read',
        module: 'FACTS',
        requiresEntitledModule: false,
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.batch.get',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
    [OID.FACT_BATCH_LINEAGE]: {
        id: OID.FACT_BATCH_LINEAGE,
        name: 'Get Fact Batch Lineage',
        labelKey: 'operations.investment.fact.batch.lineage.label',
        descriptionKey: 'operations.investment.fact.batch.lineage.description',
        permission: 'organization:facts:read',
        permissionId: 'organization:facts:read',
        module: 'FACTS',
        requiredAal: AuthenticationAssuranceLevel.AAL1,
        scopeTypes: [ScopeType.ORGANIZATION, ScopeType.BRANCH],
        requestedScope: {
            organizationId: [
                { source: 'path', field: 'organizationId' },
                { source: 'query', field: 'organizationId' },
            ],
            branchId: [
                { source: 'path', field: 'branchId' },
                { source: 'query', field: 'branchId' },
            ],
        },
        forcesClientScope: false,
        makerCheckerRequired: false,
        idempotencyPolicy: IdempotencyPolicy.NOT_APPLICABLE,
        concurrencyPolicy: ConcurrencyPolicy.NONE,
        auditPolicy: AuditPolicy.READ,
        privacyClassification: PrivacyClassification.RESTRICTED,
        telemetryId: 'investment.fact.batch.lineage',
        allowedActorTypes: [ActorType.USER, ActorType.SUPPORT_DELEGATE],
        httpMethods: ['GET'],
        isReadOnly: true,
        isFinancial: true,
        supportDelegationEligible: true,
    },
};
